import { esc } from '../esc';
import { formatMoney } from '../methods';
import { invoiceDownloadUrl, listDeposits, listWithdrawals, toPaymentError } from '../payments-api';
import type { DepositOrder, Withdrawal } from '../types';
import { ALERT_ERR_CLS } from './classes';

export interface PaymentHistoryOptions {
  /** API prefix for invoice links (default '/api'). */
  apiBase?: string;
  take?: number;
  /** Include withdrawals (default true). */
  withdrawals?: boolean;
  locale?: string;
}

export interface PaymentHistoryHandle {
  destroy(): void;
  refresh(): Promise<void>;
}

export interface HistoryRow {
  kind: 'deposit' | 'withdrawal';
  id: string;
  status: string;
  amountCents: number;
  currency: string;
  createdAt: string;
  invoiceNumber: string | null;
  detail: string;
}

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Pending', PROCESSING: 'Processing', APPROVED: 'Completed', DECLINED: 'Declined', ERROR: 'Failed',
  EXPIRED: 'Expired', MISMATCH: 'Under review', REJECTED: 'Rejected', FAILED: 'Failed',
  PARTIALLY_REFUNDED: 'Partly refunded', REFUNDED: 'Refunded',
};

/** Deposits + withdrawals, newest first. Masked payout details only (never a full card/IBAN). */
export function historyRows(deposits: DepositOrder[], withdrawals: Withdrawal[]): HistoryRow[] {
  const rows: HistoryRow[] = [
    ...deposits.map((d) => ({
      kind: 'deposit' as const, id: d.orderId, status: d.status, amountCents: d.amountCents, currency: d.currency,
      createdAt: d.createdAt, invoiceNumber: d.invoiceNumber ?? null, detail: d.method,
    })),
    ...withdrawals.map((w) => {
      const det = (w.details ?? {}) as Record<string, unknown>;
      const last4 = typeof det.last4 === 'string' ? ` •••• ${det.last4}` : '';
      return {
        kind: 'withdrawal' as const, id: w.id, status: w.status, amountCents: w.amountCents, currency: w.currency,
        createdAt: w.createdAt, invoiceNumber: w.invoiceNumber ?? null, detail: `${w.method ?? 'payout'}${last4}`,
      };
    }),
  ];
  return rows.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export function mountPaymentHistory(el: HTMLElement, opts: PaymentHistoryOptions = {}): PaymentHistoryHandle {
  let destroyed = false;

  const render = (rows: HistoryRow[]) => {
    if (!rows.length) {
      el.innerHTML = '<p class="text-sm text-text-dim">No payments yet.</p>';
      return;
    }
    el.innerHTML = `
      <ul class="flex flex-col gap-2" role="list">
        ${rows.map((r) => `
          <li data-row="${esc(r.id)}" class="flex items-center gap-3 px-3 py-2 bg-surface border border-border rounded">
            <span class="flex-1 min-w-0">
              <span class="block text-text">${r.kind === 'deposit' ? 'Deposit' : 'Withdrawal'} · ${esc(formatMoney(r.amountCents, r.currency, opts.locale))}</span>
              <span class="block text-xs text-text-dim">${esc(new Date(r.createdAt).toLocaleDateString(opts.locale))} · ${esc(r.detail)} · ${esc(STATUS_LABEL[r.status] ?? r.status)}</span>
            </span>
            ${r.invoiceNumber ? `<a data-role="invoice" href="${esc(invoiceDownloadUrl(r.invoiceNumber, opts.apiBase))}" download
              class="shrink-0 min-h-[44px] inline-flex items-center px-3 text-[13px] underline">Invoice</a>` : ''}
          </li>`).join('')}
      </ul>`;
  };

  const refresh = async () => {
    try {
      const [deps, wds] = await Promise.all([
        listDeposits(opts.take),
        opts.withdrawals === false ? Promise.resolve([] as Withdrawal[]) : listWithdrawals(),
      ]);
      if (!destroyed) render(historyRows(deps, wds));
    } catch (err) {
      if (destroyed) return;
      el.innerHTML = `<div class="${ALERT_ERR_CLS}" role="alert">${esc(toPaymentError(err).message)}</div>`;
    }
  };

  void refresh();
  return {
    destroy() { destroyed = true; el.innerHTML = ''; },
    refresh,
  };
}
